import { useState, useMemo } from "react";
import { X } from "lucide-react";

interface ContentBlock {
  type: string;
  content?: string;
  title?: string;
  mermaid_code?: string;
  image_prompt?: string;
  image_url?: string;
  alt_text?: string;
  suggested_style?: string;
  image_source?: string;
  position?: string;
}

interface ContentBlockRendererProps {
  blocks: ContentBlock[];
  postId?: string;
  editable?: boolean;
  onBlocksChange?: (blocks: ContentBlock[]) => void;
}

/** Parse simple Mermaid mindmap code into a tree structure for visual rendering */
const parseMindmapToTree = (code: string) => {
  if (!code) return null;
  
  const lines = code
    .split("\n")
    .map((l) => l.replace(/\r/, ""))
    .filter((l) => l.trim() && !l.trim().startsWith("mindmap") && !l.trim().startsWith("%%"));

  if (lines.length === 0) return null;

  interface Node {
    label: string;
    depth: number;
    children: Node[];
  }

  const getDepth = (line: string) => {
    const match = line.match(/^(\s*)/);
    return match ? Math.floor(match[1].length / 2) : 0;
  };

  const cleanLabel = (line: string) =>
    line.trim()
      .replace(/^root\((.*)\)$/, "$1")
      .replace(/^\((.*)\)$/, "$1")
      .replace(/^\[\[(.*)\]\]$/, "$1")
      .replace(/^\[(.*)\]$/, "$1")
      .replace(/^\{\{(.*)\}\}$/, "$1")
      .replace(/^\{(.*)\}$/, "$1")
      .replace(/^["'](.*)["']$/, "$1");

  const nodes: Node[] = lines.map((line) => ({
    label: cleanLabel(line),
    depth: getDepth(line),
    children: [],
  }));

  const root = nodes[0];
  if (!root) return null;

  const stack: Node[] = [root];

  for (let i = 1; i < nodes.length; i++) {
    const node = nodes[i];
    while (stack.length > 1 && stack[stack.length - 1].depth >= node.depth) {
      stack.pop();
    }
    stack[stack.length - 1].children.push(node);
    stack.push(node);
  }

  return root;
};

const BRANCH_COLORS = [
  "bg-primary/10 border-primary/30 text-primary",
  "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300",
  "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300",
  "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/30 dark:border-rose-800 dark:text-rose-300",
  "bg-violet-50 border-violet-200 text-violet-800 dark:bg-violet-950/30 dark:border-violet-800 dark:text-violet-300",
  "bg-sky-50 border-sky-200 text-sky-800 dark:bg-sky-950/30 dark:border-sky-800 dark:text-sky-300",
];

const MindmapNode = ({ node, depth = 0, colorIndex = 0 }: { node: any; depth?: number; colorIndex?: number }) => {
  const color = BRANCH_COLORS[colorIndex % BRANCH_COLORS.length];
  const isRoot = depth === 0;

  return (
    <div className={`${depth > 0 ? "ml-4 sm:ml-6" : ""}`}>
      <div className="flex items-start gap-2">
        {depth > 0 && (
          <div className="mt-2.5 flex items-center">
            <div className="h-px w-3 bg-border" />
            <div className="h-2 w-2 rounded-full bg-muted-foreground/30 shrink-0" />
          </div>
        )}
        <div
          className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-medium ${
            isRoot
              ? "bg-primary text-primary-foreground border-primary shadow-sm text-base px-4 py-2"
              : color
          }`}
        >
          {node.label}
        </div>
      </div>
      {node.children?.length > 0 && (
        <div className={`${depth > 0 ? "ml-4 border-l border-border pl-1" : "ml-2 pl-1"} mt-1 space-y-1`}>
          {node.children.map((child: any, i: number) => (
            <MindmapNode
              key={i}
              node={child}
              depth={depth + 1}
              colorIndex={isRoot ? i : colorIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SimpleMindmap = ({ code }: { code: string }) => {
  const tree = useMemo(() => parseMindmapToTree(code), [code]);

  if (!tree) {
    return (
      <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        <p className="italic">Mapa mental não disponível</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 overflow-x-auto">
      <p className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <span className="inline-block h-4 w-4 rounded bg-primary/20 text-center text-[10px] leading-4 text-primary">🗺</span>
        Mapa Mental
      </p>
      <MindmapNode node={tree} />
    </div>
  );
};

const ContentBlockRenderer = ({ blocks, postId, editable = false, onBlocksChange }: ContentBlockRendererProps) => {
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  const handleDeleteImage = (index: number) => {
    if (!onBlocksChange) return;
    const newBlocks = blocks.filter((_, i) => i !== index);
    onBlocksChange(newBlocks);
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  // Filter out image blocks that have errored (broken URLs) when not editable
  const visibleBlocks = blocks.filter((block, i) => {
    if (block.type === "image" && !block.image_url) return false;
    if (block.type === "image" && imageErrors.has(i) && !editable) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {visibleBlocks.map((block, visibleIdx) => {
        // Find original index for error tracking
        const originalIndex = blocks.indexOf(block);

        switch (block.type) {
          case "intro":
            return (
              <div key={originalIndex} className="rounded-xl border-l-4 border-primary bg-primary/5 p-5 text-lg leading-relaxed text-foreground">
                <div dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "paragraph":
            return (
              <div key={originalIndex} className="prose prose-slate max-w-none text-foreground leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "highlight":
            return (
              <div key={originalIndex} className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-center text-lg font-semibold italic text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <div dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "card":
            return (
              <div key={originalIndex} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                {block.title && <h3 className="mb-2 text-lg font-bold text-foreground">{block.title}</h3>}
                <div className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "quote":
            return (
              <blockquote key={originalIndex} className="rounded-r-xl border-l-4 border-primary bg-muted/50 p-5 text-lg italic text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </blockquote>
            );
          case "note":
            return (
              <div key={originalIndex} className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
                <span className="text-lg shrink-0">📝</span>
                <div dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "cta":
            return (
              <div key={originalIndex} className="rounded-xl bg-primary p-6 text-center text-primary-foreground">
                <div className="text-lg font-bold" dangerouslySetInnerHTML={{ __html: block.content || "" }} />
              </div>
            );
          case "mindmap":
            return <SimpleMindmap key={originalIndex} code={block.mermaid_code || ""} />;
          case "image":
            if (!block.image_url) return null;
            return (
              <figure key={originalIndex} className="group relative my-6">
                {editable && (
                  <button
                    onClick={() => handleDeleteImage(originalIndex)}
                    className="absolute top-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
                    title="Remover imagem"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <img
                  src={block.image_url}
                  alt={block.alt_text || "Imagem do artigo"}
                  className="w-full rounded-xl object-cover"
                  loading="lazy"
                  onError={() => handleImageError(originalIndex)}
                />
                {block.image_source && (
                  <figcaption className="mt-2 text-center text-xs text-muted-foreground">
                    Fonte: {block.image_source}
                  </figcaption>
                )}
              </figure>
            );
          default:
            return block.content ? (
              <div key={originalIndex} className="prose prose-slate max-w-none text-foreground">
                <div dangerouslySetInnerHTML={{ __html: block.content }} />
              </div>
            ) : null;
        }
      })}
    </div>
  );
};

export default ContentBlockRenderer;
